import { useNovel } from '@/context/novel-context';
import { useSceneSnapshot } from '@/features/scenes/hooks/use-scene-snapshot';
import { useFirebase } from '@/firebase';
import { useToast } from '@/hooks/use-toast';
import { Scene, ScenePack } from '@/lib/types';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSubscription } from './useSubscription';

export type WritersRoomPhase = 'idle' | 'snapshot' | 'prepared' | 'writing' | 'editing' | 'validating' | 'approved' | 'needs_fixes' | 'auto_fixing' | 'ready';

export function useWritersRoom(novelId: string | null, sceneId: string | null, textId: string | null) {
  const { backend, auth } = useFirebase();
  const { novel } = useNovel();
  const { toast } = useToast();
  const { t } = useTranslation();
  const { createSnapshot, isCreating: isCreatingSnapshot } = useSceneSnapshot();
  const { isTokenDepleted } = useSubscription();

  const [phase, setPhase] = useState<WritersRoomPhase>('idle');
  const [attempts, setAttempts] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSnapshotCreated, setIsSnapshotCreated] = useState(false);
  const [validation, setValidation] = useState<ScenePack['validation']>(undefined);

  const layoutMode = novel.storageLayout.narrative.scenes.items;

  // Subscribe to Scene/Pack changes
  useEffect(() => {
    if (!novel.id || !sceneId || !backend || !auth?.currentUser) return;

    const unsubscribe = backend.narrative.subscribeNarrativeItem<ScenePack>(
      novel.ownerId,
      novel.id,
      novel.storageLayout,
      'scenes',
      sceneId,
      (data) => {
        if (data) {
          const roomsStatus = data.status;

          setIsSnapshotCreated(!!data.isSnapshotCreated);

          if (roomsStatus) {
            setPhase(roomsStatus.phase || 'idle');
            setAttempts(roomsStatus.attempts || 0);
            setError(roomsStatus.error || null);
          }

          if (data.validation) {
            setValidation(data.validation);
          } else {
            setValidation(undefined);
          }
        }
      },
      (err) => {
        console.error('Failed to load scene pack in writer room:', err);
      }
    );

    return () => unsubscribe();
  }, [novel.ownerId, novel.id, layoutMode, sceneId, backend, auth]);

  const handlePrepare = useCallback(async (scene: Scene, forceSnapshot: boolean = false) => {
    if (!novel.id || !sceneId || !backend) return;
    setIsProcessing(true);
    setError(null);

    try {
      // 1. Snapshot if needed (or if forced)
      if (forceSnapshot || (!scene.isSnapshotCreated && !isSnapshotCreated)) {
        setPhase('snapshot');
        await createSnapshot(scene);
      }

      // 2. Prepare (Voices)
      setPhase('prepared');
      toast({ title: t('writers_room.preparation_success') });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      console.error('Preparation failed:', err);
      setError(message);
      toast({ title: t('writers_room.preparation_error'), variant: 'destructive' });
    } finally {
      setIsProcessing(false);
    }
  }, [novel, sceneId, backend, createSnapshot, t, toast, isSnapshotCreated]);

  const handleExecute = useCallback(async (targetPhase: WritersRoomPhase = 'writing', forceReset: boolean = false) => {
    if (!novel.id || !sceneId || !textId || !backend || !auth?.currentUser) return;
    setIsProcessing(true);
    setError(null);

    try {
      // Call the decoupled backend execute flow
      await backend.ai.executeWritersRoom({
        userId: novel.ownerId,
        novelId: novel.id,
        sceneId,
        phase: targetPhase,
        forceReset
      });

      toast({ title: t('writers_room.execution_success') });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      console.error('Execution failed:', err);
      setError(message);
      toast({ title: t('writers_room.execution_error'), variant: 'destructive' });
    } finally {
      setIsProcessing(false);
    }
  }, [novel, sceneId, textId, backend, t, toast, auth]);

  const removeValidationIssue = useCallback(async (category: 'continuity' | 'lore', issueId: string) => {
    if (!novel.id || !sceneId || !backend || !auth?.currentUser || !validation) return;

    // Deep clone validation to modify it
    const newValidation = JSON.parse(JSON.stringify(validation)) as ScenePack['validation'];

    if (category === 'continuity' && newValidation?.continuity) {
      newValidation.continuity.issues = newValidation.continuity.issues.filter(i => i.id !== issueId);
    } else if (category === 'lore' && newValidation?.lore) {
      newValidation.lore.issues = newValidation.lore.issues.filter(i => i.id !== issueId);
    }

    const totalIssues = (newValidation?.continuity?.issues?.length || 0) + (newValidation?.lore?.issues?.length || 0);

    const updates: Record<string, unknown> = { validation: newValidation };
    if (totalIssues === 0) {
      updates['status.phase'] = 'approved';
    }

    try {
      await backend.narrative.updateNarrativeItem(novel.ownerId, novel, 'scenes', sceneId, updates);
      toast({ title: t('writers_room.issue_removed') });
    } catch (err: unknown) {
      console.error('Failed to remove issue:', err);
      toast({ title: t('common.error'), variant: 'destructive' });
    }
  }, [novel, sceneId, backend, auth, validation, toast, t]);

  return {
    phase,
    attempts,
    error,
    validation,
    isSnapshotCreated,
    isProcessing: isProcessing || isCreatingSnapshot,
    isTokenDepleted,
    handlePrepare,
    handleExecute,
    removeValidationIssue,
  };
}
